import get from 'lodash-es/get';
import {
    IdentityAssessor,
    IdentityOrganization,
    IdentityPerson,
    IdentityTeamContact,
    Location,
    Team,
    TeamType,
} from '@reg/shared/model';

/**
 * Model to hold Team data for wrestling. It's used for grouping/categorizing
 * wrestlers together onto a single team
 */
export class IdentityTeam {
    public id: string = null;
    public location: Location;
    public teamType: TeamType;
    public sportId: string;
    public coaches: IdentityPerson[] = [];
    public userId: string;
    public hasModifyAccess: boolean;
    public assessors: IdentityAssessor[] = [];
    public organizations: IdentityOrganization[] = [];
    public contacts: IdentityTeamContact[] = [];

    private _name: string = null;
    set name(name: string) {
        this._name = name && name.trim();
    }

    get name(): string {
        return this._name;
    }

    private _abbreviation: string = null;
    set abbreviation(abbreviation: string) {
        this._abbreviation = abbreviation && abbreviation.trim();
    }

    get abbreviation(): string {
        return this._abbreviation;
    }

    public getFormattedLocation(): string {
        if (this.location) {
            return this.location.format();
        }

        return null;
    }

    public formatNameAbbreviation(): string {
        if (this.abbreviation) {
            return `${this.name || ''} (${this.abbreviation})`;
        }

        return this.name;
    }

    /**
     * Constructor
     */
    constructor() {
        this.location = new Location();
        this.teamType = new TeamType();
    }

    public isTeamTypeHSOrLower(): boolean {
        return this.teamType &&
            (this.teamType.id === TeamType.HIGH_SCHOOL_ID ||
                this.teamType.id === TeamType.MIDDLE_SCHOOL_ID ||
                this.teamType.id === TeamType.ELEMENTARY_YOUTH_ID);
    }

    public fillFromTeam(team: Team): IdentityTeam {
        if (team) {
            this.name = team.name;
            this.abbreviation = team.abbreviation;

            if (team.location) {
                this.location = team.location.clone();
            }
        }

        return this;
    }

    /**
     * Determines if the given organization is linked to this team
     * @param {string} identityOrganizationId
     * @returns {boolean}
     */
    public isInOrganization(identityOrganizationId: string): boolean {
        // If the team is part of the MIAA organization display the reports tab
        if (this.organizations &&
            this.organizations.filter((o: IdentityOrganization) => o.id === identityOrganizationId).length > 0) {
            return true;
        }

        return false;
    }

    /**
     * Clone Team object
     */
    public clone(): IdentityTeam {
        const clone: IdentityTeam = new IdentityTeam();
        clone.id = this.id;
        clone.name = this.name;
        clone.abbreviation = this.abbreviation;
        clone.sportId = this.sportId;
        clone.userId = this.userId;
        clone.hasModifyAccess = this.hasModifyAccess;

        if (this.location) {
            clone.location = this.location.clone();
        }

        if (this.teamType) {
            clone.teamType = this.teamType.clone();
        }

        if (this.coaches) {
            clone.coaches = this.coaches.map((c: IdentityPerson) => c.clone());
        }

        if (this.assessors) {
            clone.assessors = this.assessors.map((a: IdentityAssessor) => a.clone());
        }

        if (this.organizations) {
            clone.organizations = this.organizations.map((o: IdentityOrganization) => o.clone());
        }

        if (this.contacts) {
            clone.contacts = this.contacts.map((c: IdentityTeamContact) => c.clone());
        }

        return clone;
    }

    /**
     * Serialize from a json api format
     */
    public serialize(): string {
        const json: any = {};
        json.meta = {};
        json.data = {};
        json.data.type = 'team';
        json.data.id = this.id;
        json.data.attributes = {};
        json.data.attributes.name = this.name;
        json.data.attributes.abbreviation = this.abbreviation;

        if (this.location) {
            json.data.attributes.location = this.location.serialize();
        }

        json.data.relationships = {};

        if (this.teamType) {
            const teamType: any = {};
            teamType.data = { type: 'teamType', id: this.teamType.id };
            json.data.relationships.teamType = teamType;
        }

        if (this.sportId) {
            const sport: any = {};
            sport.data = { type: 'sport', id: this.sportId };
            json.data.relationships.sport = sport;
        }

        json.meta.role = 'coach';

        return JSON.stringify(json);
    }

    /**
     * Deserialize to json api format
     */
    public deserialize(json: any, included: any[], identitySportName: string): IdentityTeam {
        const data: any = json.data || json;
        const attrs: any = data.attributes || {};
        included = included || json.included || [];
        const relationships: any = data.relationships || {};
        const meta: any = json.meta;

        this.id = data.id;
        this.name = attrs.name;
        this.abbreviation = attrs.abbreviation;
        this.teamType.id = attrs.teamTypeId;
        this.sportId = attrs.sportId;
        this.location = (new Location()).deserialize(attrs.location);

        // Fill TeamType from relationships if it exists
        const teamTypeLink: any = relationships.teamType;
        if (teamTypeLink &&
            included &&
            teamTypeLink.data &&
            teamTypeLink.data.id) {
            const teamTypeId: string = teamTypeLink.data.id;
            const teamTypes: any[] = included.filter((i: any) => i.type === 'teamType' && i.id === teamTypeId);

            if (teamTypes.length === 1) {
                this.teamType = (new TeamType()).deserialize(teamTypes[0]);
            }
        }

        const coachLinks: any = relationships.coaches;
        if (coachLinks &&
            included &&
            coachLinks.data) {
            this.coaches = [];

            for (const coachLink of coachLinks.data) {
                const coach: any = included.filter((i: any) => i.id === coachLink.id)[0];
                if (coach) {
                    this.coaches.push((new IdentityPerson()).deserialize(coach, included, identitySportName));
                }
            }
        }

        const assessorLinks: any = relationships.assessors;
        if (assessorLinks &&
            included &&
            assessorLinks.data) {
            this.assessors = [];

            for (const assessorLink of assessorLinks.data) {
                const assessor: any = included.filter((i: any) => i.id === assessorLink.id)[0];
                if (assessor) {
                    this.assessors.push((new IdentityAssessor()).deserialize(assessor, included));
                }
            }
        }

        const organizationLinks: any = relationships.organizations;
        if (organizationLinks &&
            included &&
            organizationLinks.data) {
            this.organizations = [];

            for (const organizationLink of organizationLinks.data) {
                const organization: any = included.filter((i: any) => i.id === organizationLink.id)[0];
                if (organization) {
                    this.organizations.push((new IdentityOrganization()).deserialize(organization));
                }
            }
        }

        const contactList: any[] = get(data, 'relationships.contacts.data') || [];
        contactList.forEach((x: any) => {
            const item: any = included.find((i: any) => i.id === x.id);

            if (item) {
                this.contacts.push((new IdentityTeamContact()).deserialize(item));
            }
        });

        if (meta && meta.userPermissions) {
            this.userId = meta.userPermissions.userId;
            this.hasModifyAccess = meta.userPermissions.hasModifyAccess;
        }

        return this;
    }
}
